import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { ArrowUpDown, ArrowUp, ArrowDown, MoreHorizontal, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useSession } from '@/lib/auth-client';
import { apiGet, apiPost, apiDelete } from '@/lib/api-client';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

// Test accounts to exclude from Pro count and revenue calculations
const TEST_ACCOUNTS = ["bm.ksglu@gmail.com", "mrtctn506@gmail.com"];

// Spinner component for loading states
function Spinner() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
    </div>
  );
}

// Date range filter type
type DateRange = 'today' | 'week' | 'month' | 'all' | 'custom';

// Custom date range type
interface CustomDateRange {
  startDate: string;
  endDate: string;
}

// Schema for admin stats API response (matches backend)
const AdminUserSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  email: z.string(),
  image: z.string().nullable(),
  country: z.string().nullable(),
  lastIpAddress: z.string().nullable(),
  lastSessionAt: z.string().nullable(),
  totalGenerations: z.coerce.number(),
  totalTokensUsed: z.coerce.number(),
  imageAnalyses: z.coerce.number(),
  videosRemaining: z.coerce.number(),
  videosLimit: z.coerce.number(),
  subscriptionPlan: z.string(),
  subscriptionStatus: z.string(),
  stripeCustomerId: z.string().nullable(),
  stripeSubscriptionId: z.string().nullable(),
  createdAt: z.string(),
  isBanned: z.boolean(),
}).passthrough();

const AdminPromptSchema = z.object({
  userEmail: z.string(),
  prompt: z.string(),
  tokensUsed: z.coerce.number(),
  createdAt: z.string(),
}).passthrough();

const AdminSummarySchema = z.object({
  totalUsers: z.coerce.number(),
  totalGenerations: z.coerce.number(),
  totalTokensUsed: z.coerce.number(),
  totalImageAnalyses: z.coerce.number(),
  activeProSubscriptions: z.coerce.number(),
}).passthrough();

const PaginationSchema = z.object({
  page: z.coerce.number(),
  pageSize: z.coerce.number(),
  totalItems: z.coerce.number(),
  totalPages: z.coerce.number(),
}).passthrough();

const FiltersSchema = z.object({
  range: z.enum(['today', 'week', 'month', 'all', 'custom']),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
  promptsUserId: z.string().nullable().optional(),
}).passthrough();

const PricingCostsSchema = z.object({
  VIDEO_GENERATION: z.coerce.number(),
  IMAGE_ANALYSIS: z.coerce.number(),
  PROMPT_ENHANCEMENT: z.coerce.number().optional(),
}).passthrough();

const PricingPlanLimitSchema = z.object({
  videosPerMonth: z.coerce.number(),
  imagesPerMonth: z.coerce.number(),
  price: z.coerce.number(),
}).passthrough();

const PricingSchema = z.object({
  costs: PricingCostsSchema,
  planLimits: z.object({
    free: PricingPlanLimitSchema,
    pro: PricingPlanLimitSchema,
  }).passthrough(),
}).passthrough();

const EventFunnelSchema = z.object({
  upgradeClicks: z.coerce.number(),
  manageSubscriptionClicks: z.coerce.number(),
  checkoutStarted: z.coerce.number(),
  checkoutCompleted: z.coerce.number(),
  cancelInitiated: z.coerce.number(),
  conversionRate: z.string(),
  rawEvents: z.array(z.object({
    event_name: z.string(),
    count: z.coerce.number(),
    unique_users: z.coerce.number(),
  })).optional(),
}).passthrough();

const AdminStatsSchema = z.object({
  summary: AdminSummarySchema,
  users: z.array(AdminUserSchema),
  recentPrompts: z.array(AdminPromptSchema),
  pagination: PaginationSchema.optional(),
  filters: FiltersSchema.optional(),
  dbMode: z.enum(['local', 'remote', 'production']).optional(),
  environment: z.enum(['development', 'staging', 'production']).optional(),
  pricing: PricingSchema.optional(),
  eventFunnel: EventFunnelSchema.optional(),
}).passthrough(); // Allow extra fields

type AdminUser = z.infer<typeof AdminUserSchema>;
type AdminPrompt = z.infer<typeof AdminPromptSchema>;
type AdminStats = z.infer<typeof AdminStatsSchema>;

type SortKey = 'name' | 'email' | 'country' | 'lastIpAddress' | 'totalGenerations' | 'totalTokensUsed' | 'videosRemaining' | 'subscriptionPlan' | 'subscriptionStatus' | 'stripeCustomerId' | 'stripeSubscriptionId' | 'createdAt';
type SortDirection = 'asc' | 'desc';

// Hook for fetching admin stats with params
function useAdminStats(params: {
  range?: DateRange;
  page?: number;
  customRange?: CustomDateRange;
  promptsUserId?: string;
  dbEnv?: DbEnvironment;
} = {}) {
  const queryParams = new URLSearchParams();
  if (params.range && params.range !== 'all') {
    queryParams.set('range', params.range);
  }
  if (params.range === 'custom' && params.customRange) {
    queryParams.set('startDate', params.customRange.startDate);
    queryParams.set('endDate', params.customRange.endDate);
  }
  if (params.page && params.page > 1) {
    queryParams.set('page', params.page.toString());
  }
  if (params.promptsUserId) {
    queryParams.set('promptsUserId', params.promptsUserId);
  }
  if (params.dbEnv) {
    queryParams.set('db', params.dbEnv);
  }
  const queryString = queryParams.toString();
  const endpoint = `/admin/stats${queryString ? `?${queryString}` : ''}`;

  return useQuery({
    queryKey: ['admin-stats', params.range, params.page, params.customRange?.startDate, params.customRange?.endDate, params.promptsUserId, params.dbEnv],
    queryFn: () => apiGet(endpoint, AdminStatsSchema),
    refetchInterval: 60000, // Refresh every minute
    staleTime: 30000,
  });
}

// Schema for user action responses (matches backend response.data)
const UserActionResponseSchema = z.object({
  userId: z.string(),
  email: z.string(),
}).passthrough(); // Allow additional fields like plan, status, isBanned, etc.

// Format relative time
function timeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

// Format date for joined column
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Format large numbers
function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

// Truncate text
function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

// Date Range Filter Component
function DateRangeFilter({
  value,
  onChange,
  customRange,
  onCustomRangeChange,
}: {
  value: DateRange;
  onChange: (range: DateRange) => void;
  customRange: CustomDateRange;
  onCustomRangeChange: (range: CustomDateRange) => void;
}) {
  const ranges: { label: string; value: DateRange }[] = [
    { label: 'Today', value: 'today' },
    { label: 'This Week', value: 'week' },
    { label: 'This Month', value: 'month' },
    { label: 'All Time', value: 'all' },
    { label: 'Custom', value: 'custom' },
  ];

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-1 border border-slate-200 rounded-lg p-1 bg-white shadow-sm">
        {ranges.map((range) => (
          <button
            key={range.value}
            onClick={() => onChange(range.value)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              value === range.value
                ? 'bg-slate-900 text-white'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            {range.label}
          </button>
        ))}
      </div>
      {value === 'custom' && (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={customRange.startDate}
            onChange={(e) => onCustomRangeChange({ ...customRange, startDate: e.target.value })}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          />
          <span className="text-slate-400">to</span>
          <input
            type="date"
            value={customRange.endDate}
            onChange={(e) => onCustomRangeChange({ ...customRange, endDate: e.target.value })}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          />
        </div>
      )}
    </div>
  );
}

// Summary Card Component
function SummaryCard({ title, value, subtitle }: { title: string; value: string | number; subtitle?: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <p className="text-sm text-slate-600 font-medium">{title}</p>
      <p className="text-3xl font-semibold text-slate-900 mt-2">{value}</p>
      {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
    </div>
  );
}

// Status Badge Component
function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    inactive: 'bg-slate-50 text-slate-600 border-slate-200',
    suspended: 'bg-red-50 text-red-700 border-red-200',
    banned: 'bg-red-50 text-red-700 border-red-200',
    none: 'bg-slate-50 text-slate-400 border-slate-200',
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${colors[status] || colors.none}`}>
      {status || 'none'}
    </span>
  );
}

// Plan Badge Component
function PlanBadge({ plan }: { plan: string }) {
  const colors: Record<string, string> = {
    free: 'bg-slate-50 text-slate-600 border-slate-200',
    pro: 'bg-violet-50 text-violet-700 border-violet-200',
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${colors[plan] || colors.free}`}>
      {plan}
    </span>
  );
}

// Sort Header Component
function SortHeader({
  label,
  sortKey,
  currentSort,
  currentDirection,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  currentSort: SortKey;
  currentDirection: SortDirection;
  onSort: (key: SortKey) => void;
}) {
  const isActive = currentSort === sortKey;

  return (
    <button
      onClick={() => onSort(sortKey)}
      className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors"
    >
      {label}
      {isActive ? (
        currentDirection === 'asc' ? (
          <ArrowUp className="w-3 h-3" />
        ) : (
          <ArrowDown className="w-3 h-3" />
        )
      ) : (
        <ArrowUpDown className="w-3 h-3 text-slate-400" />
      )}
    </button>
  );
}

// Confirmation Dialog Component
function ConfirmationDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  isDestructive = false,
  isLoading = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: () => void;
  isDestructive?: boolean;
  isLoading?: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="text-slate-900">{title}</DialogTitle>
          <DialogDescription className="text-slate-600">{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className="border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </Button>
          <Button
            variant={isDestructive ? 'destructive' : 'default'}
            onClick={onConfirm}
            disabled={isLoading}
            className={isDestructive ? '' : 'bg-slate-900 text-white hover:bg-slate-800'}
          >
            {isLoading ? 'Processing...' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// User Actions Dropdown Component
function UserActionsDropdown({ user, dbEnv }: { user: AdminUser; dbEnv: DbEnvironment }) {
  const [dialogConfig, setDialogConfig] = useState<{
    open: boolean;
    action: 'changeToPro' | 'changeToFree' | 'ban' | 'unban' | 'delete' | null;
  }>({ open: false, action: null });

  const queryClient = useQueryClient();

  const changePlanMutation = useMutation({
    mutationFn: async ({ userId, plan }: { userId: string; plan: 'pro' | 'free' }) => {
      return apiPost(`/admin/users/${userId}/plan?db=${dbEnv}`, { plan }, UserActionResponseSchema);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['admin-stats'] });
      setDialogConfig({ open: false, action: null });
    },
    onError: (error) => {
      console.error('Change plan error:', error);
      alert('Failed to change plan. Check console for details.');
    },
  });

  const banUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return apiPost(`/admin/users/${userId}/ban?db=${dbEnv}`, {}, UserActionResponseSchema);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['admin-stats'] });
      setDialogConfig({ open: false, action: null });
    },
    onError: (error) => {
      console.error('Ban user error:', error);
      alert('Failed to ban user. Check console for details.');
    },
  });

  const unbanUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return apiPost(`/admin/users/${userId}/unban?db=${dbEnv}`, {}, UserActionResponseSchema);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['admin-stats'] });
      setDialogConfig({ open: false, action: null });
    },
    onError: (error) => {
      console.error('Unban user error:', error);
      alert('Failed to unban user. Check console for details.');
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return apiDelete(`/admin/users/${userId}?db=${dbEnv}`, UserActionResponseSchema);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['admin-stats'] });
      setDialogConfig({ open: false, action: null });
    },
    onError: (error) => {
      console.error('Delete user error:', error);
      alert('Failed to delete user. Check console for details.');
    },
  });

  const handleConfirm = () => {
    switch (dialogConfig.action) {
      case 'changeToPro':
        changePlanMutation.mutate({ userId: user.id, plan: 'pro' });
        break;
      case 'changeToFree':
        changePlanMutation.mutate({ userId: user.id, plan: 'free' });
        break;
      case 'ban':
        banUserMutation.mutate(user.id);
        break;
      case 'unban':
        unbanUserMutation.mutate(user.id);
        break;
      case 'delete':
        deleteUserMutation.mutate(user.id);
        break;
    }
  };

  const isLoading = changePlanMutation.isPending || banUserMutation.isPending || unbanUserMutation.isPending || deleteUserMutation.isPending;

  const dialogDetails = {
    changeToPro: {
      title: 'Change to Pro Plan',
      description: `Are you sure you want to upgrade ${user.email} to the Pro plan?`,
      confirmLabel: 'Change to Pro',
      isDestructive: false,
    },
    changeToFree: {
      title: 'Change to Free Plan',
      description: `Are you sure you want to downgrade ${user.email} to the Free plan?`,
      confirmLabel: 'Change to Free',
      isDestructive: false,
    },
    ban: {
      title: 'Ban User',
      description: `Are you sure you want to ban ${user.email}? This will prevent them from accessing the service.`,
      confirmLabel: 'Ban User',
      isDestructive: true,
    },
    unban: {
      title: 'Unban User',
      description: `Are you sure you want to unban ${user.email}? They will regain access to the service.`,
      confirmLabel: 'Unban User',
      isDestructive: false,
    },
    delete: {
      title: 'Delete User',
      description: `Are you sure you want to permanently delete ${user.email}? This action cannot be undone and all user data will be lost.`,
      confirmLabel: 'Delete User',
      isDestructive: true,
    },
  };

  const currentDialog = dialogConfig.action ? dialogDetails[dialogConfig.action] : null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="p-1 rounded-lg hover:bg-slate-100 transition-colors">
            <MoreHorizontal className="w-4 h-4 text-slate-500" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 bg-white border-slate-200">
          <DropdownMenuItem
            onClick={() => setDialogConfig({ open: true, action: 'changeToPro' })}
            className="text-slate-700 hover:bg-slate-50 cursor-pointer"
            disabled={user.subscriptionPlan === 'pro'}
          >
            Change to Pro
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setDialogConfig({ open: true, action: 'changeToFree' })}
            className="text-slate-700 hover:bg-slate-50 cursor-pointer"
            disabled={user.subscriptionPlan === 'free'}
          >
            Change to Free
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-slate-200" />
          {user.isBanned ? (
            <DropdownMenuItem
              onClick={() => setDialogConfig({ open: true, action: 'unban' })}
              className="text-emerald-600 cursor-pointer"
            >
              Unban User
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={() => setDialogConfig({ open: true, action: 'ban' })}
              variant="destructive"
              className="cursor-pointer"
            >
              Ban User
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => setDialogConfig({ open: true, action: 'delete' })}
            variant="destructive"
            className="cursor-pointer"
          >
            Delete User
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {currentDialog && (
        <ConfirmationDialog
          open={dialogConfig.open}
          onOpenChange={(open) => setDialogConfig({ open, action: open ? dialogConfig.action : null })}
          title={currentDialog.title}
          description={currentDialog.description}
          confirmLabel={currentDialog.confirmLabel}
          onConfirm={handleConfirm}
          isDestructive={currentDialog.isDestructive}
          isLoading={isLoading}
        />
      )}
    </>
  );
}

// Truncate for stripe IDs
function truncateId(id: string | null, maxLength: number = 12): string {
  if (!id) return '-';
  if (id.length <= maxLength) return id;
  return id.slice(0, maxLength) + '...';
}

// Users Table Component
function UsersTable({ users, dbEnv }: { users: AdminUser[]; dbEnv: DbEnvironment }) {
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      let aVal: string | number | null = a[sortKey];
      let bVal: string | number | null = b[sortKey];

      // Handle null values
      if (aVal === null) aVal = '';
      if (bVal === null) bVal = '';

      // Handle dates
      if (sortKey === 'createdAt') {
        aVal = new Date(aVal as string).getTime();
        bVal = new Date(bVal as string).getTime();
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      return 0;
    });
  }, [users, sortKey, sortDirection]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50">
              <th className="px-3 py-3 text-left text-xs font-medium text-slate-600">Avatar</th>
              <th className="px-3 py-3 text-left">
                <SortHeader label="Name" sortKey="name" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              </th>
              <th className="px-3 py-3 text-left">
                <SortHeader label="Email" sortKey="email" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              </th>
              <th className="px-3 py-3 text-left">
                <SortHeader label="Country" sortKey="country" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              </th>
              <th className="px-3 py-3 text-left">
                <SortHeader label="IP Address" sortKey="lastIpAddress" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              </th>
              <th className="px-3 py-3 text-left">
                <SortHeader label="Gens" sortKey="totalGenerations" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              </th>
              <th className="px-3 py-3 text-left">
                <SortHeader label="Tokens" sortKey="totalTokensUsed" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              </th>
              <th className="px-3 py-3 text-left">
                <SortHeader label="Videos" sortKey="videosRemaining" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              </th>
              <th className="px-3 py-3 text-left">
                <SortHeader label="Plan" sortKey="subscriptionPlan" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              </th>
              <th className="px-3 py-3 text-left">
                <SortHeader label="Status" sortKey="subscriptionStatus" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              </th>
              <th className="px-3 py-3 text-left">
                <SortHeader label="Stripe Customer" sortKey="stripeCustomerId" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              </th>
              <th className="px-3 py-3 text-left">
                <SortHeader label="Stripe Sub" sortKey="stripeSubscriptionId" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              </th>
              <th className="px-3 py-3 text-left">
                <SortHeader label="Joined" sortKey="createdAt" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sortedUsers.map((user) => (
              <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-3 py-3">
                  {user.image ? (
                    <img
                      src={user.image}
                      alt={user.name || 'User'}
                      className="w-8 h-8 rounded-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                      <span className="text-xs text-slate-500 font-medium">
                        {(user.name || user.email).charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 text-sm text-slate-900 font-medium">
                  {user.name || '-'}
                </td>
                <td className="px-3 py-3 text-sm text-slate-600">{user.email}</td>
                <td className="px-3 py-3 text-sm text-slate-600">{user.country || '-'}</td>
                <td className="px-3 py-3 text-xs text-slate-500 font-mono">{user.lastIpAddress || '-'}</td>
                <td className="px-3 py-3 text-sm">
                  <span className="text-slate-900 font-medium">{formatNumber(user.totalGenerations)}</span>
                  <span className="text-slate-400 text-xs ml-1">vid</span>
                  <span className="text-slate-300 mx-1">/</span>
                  <span className="text-slate-700">{formatNumber(user.imageAnalyses)}</span>
                  <span className="text-slate-400 text-xs ml-1">img</span>
                </td>
                <td className="px-3 py-3 text-sm text-slate-600">{formatNumber(user.totalTokensUsed)}</td>
                <td className="px-3 py-3 text-sm">
                  <span className={`font-medium ${user.videosRemaining === 0 ? 'text-red-600' : user.videosRemaining <= 1 ? 'text-amber-600' : 'text-slate-900'}`}>
                    {user.videosRemaining}
                  </span>
                  <span className="text-slate-400">/{user.videosLimit}</span>
                  <span className="text-xs text-slate-400 ml-1">left</span>
                </td>
                <td className="px-3 py-3">
                  <PlanBadge plan={user.subscriptionPlan} />
                </td>
                <td className="px-3 py-3">
                  <StatusBadge status={user.isBanned ? 'banned' : user.subscriptionStatus} />
                </td>
                <td className="px-3 py-3">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-xs text-slate-500 font-mono cursor-help">
                        {truncateId(user.stripeCustomerId)}
                      </span>
                    </TooltipTrigger>
                    {user.stripeCustomerId && (
                      <TooltipContent side="top" className="bg-slate-900 text-white">
                        <p className="text-xs font-mono">{user.stripeCustomerId}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </td>
                <td className="px-3 py-3">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-xs text-slate-500 font-mono cursor-help">
                        {truncateId(user.stripeSubscriptionId)}
                      </span>
                    </TooltipTrigger>
                    {user.stripeSubscriptionId && (
                      <TooltipContent side="top" className="bg-slate-900 text-white">
                        <p className="text-xs font-mono">{user.stripeSubscriptionId}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </td>
                <td className="px-3 py-3 text-sm text-slate-400">{formatDate(user.createdAt)}</td>
                <td className="px-3 py-3">
                  <UserActionsDropdown user={user} dbEnv={dbEnv} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sortedUsers.length === 0 && (
        <div className="px-4 py-8 text-center text-sm text-slate-400">
          No users found
        </div>
      )}
    </div>
  );
}

// Pagination Component
function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
      <p className="text-sm text-slate-600">
        Page {currentPage} of {totalPages}
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Previous
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Next
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// Recent Prompts Component
function RecentPrompts({
  prompts,
  pagination,
  onPageChange,
  users,
  selectedUserId,
  onUserFilterChange,
}: {
  prompts: AdminPrompt[];
  pagination?: { page: number; totalPages: number };
  onPageChange: (page: number) => void;
  users: AdminUser[];
  selectedUserId: string;
  onUserFilterChange: (userId: string) => void;
}) {
  const currentPage = pagination?.page || 1;
  const totalPages = pagination?.totalPages || 1;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
      <div className="px-6 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Recent Prompts</h2>
          <p className="text-sm text-slate-400 mt-0.5">Generation prompts from users</p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="user-filter" className="text-sm text-slate-600">Filter by user:</label>
          <select
            id="user-filter"
            value={selectedUserId}
            onChange={(e) => onUserFilterChange(e.target.value)}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          >
            <option value="">All Users</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.email}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {prompts.map((prompt, index) => (
          <div key={`${prompt.userEmail}-${prompt.createdAt}-${index}`} className="px-6 py-4 hover:bg-slate-50 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600">{prompt.userEmail}</span>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-400">{formatNumber(prompt.tokensUsed)} tokens</span>
                <span className="text-xs text-slate-400">{timeAgo(prompt.createdAt)}</span>
              </div>
            </div>
            <p className="text-sm text-slate-900">{truncate(prompt.prompt, 150)}</p>
          </div>
        ))}
      </div>
      {prompts.length === 0 && (
        <div className="px-6 py-8 text-center text-sm text-slate-400">
          No prompts found
        </div>
      )}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}

// Access Denied Component
function AccessDenied() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-semibold text-slate-900">Access Denied</h1>
        <p className="text-slate-600 mt-2">You do not have permission to view this page.</p>
      </div>
    </div>
  );
}

// DB Environment type
type DbEnvironment = 'local' | 'remote';

// Main Admin Page Component
export function AdminPage() {
  const { data: session, isPending: isSessionPending } = useSession();
  const [dateRange, setDateRange] = useState<DateRange>('all');
  const [customRange, setCustomRange] = useState<CustomDateRange>({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });
  const [promptsPage, setPromptsPage] = useState(1);
  const [promptsUserId, setPromptsUserId] = useState('');
  const [dbEnvironment, setDbEnvironment] = useState<DbEnvironment>('local');

  const { data: statsResponse, isPending: isStatsPending, error } = useAdminStats({
    range: dateRange,
    page: promptsPage,
    customRange: dateRange === 'custom' ? customRange : undefined,
    promptsUserId: promptsUserId || undefined,
    dbEnv: dbEnvironment,
  });

  // Show spinner while loading session or stats
  if (isSessionPending || isStatsPending) {
    return <Spinner />;
  }

  // Check if user is not logged in
  if (!session?.user) {
    return <AccessDenied />;
  }

  // Handle error state (including 403 Forbidden for non-admin users)
  if (error || !statsResponse?.data) {
    // Check if it's an authorization error
    const isAuthError = error && 'status' in error && (error.status === 403 || error.status === 401);

    if (isAuthError) {
      return <AccessDenied />;
    }

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-lg">
          <h1 className="text-2xl font-semibold text-slate-900">Error Loading Stats</h1>
          <p className="text-slate-600 mt-2">Unable to fetch admin statistics.</p>
          {error && (
            <pre className="mt-4 p-4 bg-slate-100 rounded-lg text-left text-xs text-slate-600 overflow-auto">
              {JSON.stringify(error, null, 2)}
            </pre>
          )}
        </div>
      </div>
    );
  }

  const stats: AdminStats = statsResponse.data;

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    setPromptsPage(1); // Reset to first page when changing date range
  };

  const handleCustomRangeChange = (range: CustomDateRange) => {
    setCustomRange(range);
    setPromptsPage(1);
  };

  const handlePromptsPageChange = (page: number) => {
    setPromptsPage(page);
  };

  const handlePromptsUserFilterChange = (userId: string) => {
    setPromptsUserId(userId);
    setPromptsPage(1);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Subtle grid background */}
      <div
        className="fixed inset-0 opacity-40 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(to right, #e2e8f0 1px, transparent 1px),
            linear-gradient(to bottom, #e2e8f0 1px, transparent 1px)
          `,
          backgroundSize: '48px 48px',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Admin Dashboard</h1>
            <p className="text-sm text-slate-500 mt-1">Overview of Vemotion AI usage and users</p>
          </div>
          {/* DB Environment Toggle - Only show in development */}
          <div className="flex flex-col items-end gap-1">
            {stats?.environment === 'production' ? (
              <div className="px-3 py-1.5 text-sm font-medium rounded-lg bg-violet-600 text-white">
                Production DB
              </div>
            ) : (
              <div className="flex items-center gap-2 border border-slate-200 rounded-lg p-1 bg-white shadow-sm">
                <button
                  onClick={() => setDbEnvironment('local')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    dbEnvironment === 'local'
                      ? 'bg-emerald-600 text-white'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Local DB
                </button>
                <button
                  onClick={() => setDbEnvironment('remote')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    dbEnvironment === 'remote'
                      ? 'bg-violet-600 text-white'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Remote DB
                </button>
              </div>
            )}
            {stats && stats.environment !== 'production' && (
              <p className="text-xs text-slate-400">
                Connected: <span className={stats.dbMode === 'remote' ? 'text-violet-600 font-medium' : 'text-emerald-600 font-medium'}>
                  {stats.dbMode === 'remote' ? 'Production D1' : 'Local D1'}
                </span>
              </p>
            )}
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="mb-8">
          <DateRangeFilter
            value={dateRange}
            onChange={handleDateRangeChange}
            customRange={customRange}
            onCustomRangeChange={handleCustomRangeChange}
          />
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <SummaryCard
            title="Total Users"
            value={formatNumber(stats.summary.totalUsers)}
            subtitle="Registered accounts"
          />
          <SummaryCard
            title="Total Generations"
            value={formatNumber(stats.summary.totalGenerations)}
            subtitle="Videos created"
          />
          <SummaryCard
            title="Total Tokens"
            value={formatNumber(stats.summary.totalTokensUsed)}
            subtitle="AI tokens consumed"
          />
          <SummaryCard
            title="Image Analyses"
            value={formatNumber(stats.summary.totalImageAnalyses)}
            subtitle="Images analyzed"
          />
        </div>

        {/* Analytics Insights */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* Top Token Users */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <p className="text-sm text-slate-600 font-medium mb-3">Top Token Users</p>
            {(() => {
              const topUsers = [...stats.users].sort((a, b) => b.totalTokensUsed - a.totalTokensUsed).slice(0, 5);
              return topUsers.length > 0 ? (
                <div className="space-y-2">
                  {topUsers.map((user, idx) => (
                    <div key={user.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs text-slate-400 w-4">{idx + 1}.</span>
                        <span className="text-sm text-slate-900 truncate">{user.email}</span>
                      </div>
                      <span className="text-xs text-slate-500 font-medium ml-2">{formatNumber(user.totalTokensUsed)}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-slate-400 text-sm">No users</p>;
            })()}
          </div>

          {/* Near Limit Users - Only shown for "All Time" since usage is current month */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-slate-600 font-medium">Near Limit (Upsell Targets)</p>
              {dateRange !== 'all' && (
                <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded">This month</span>
              )}
            </div>
            {(() => {
              const nearLimit = stats.users
                .filter(u => {
                  if (u.subscriptionPlan !== 'free' || u.videosLimit === 0) return false;
                  const usagePercent = (u.videosLimit - u.videosRemaining) / u.videosLimit;
                  // Show users who used 50%+ of their quota
                  return usagePercent >= 0.5;
                })
                .sort((a, b) => a.videosRemaining - b.videosRemaining)
                .slice(0, 5);
              return nearLimit.length > 0 ? (
                <div className="space-y-2">
                  {nearLimit.map((user) => (
                    <div key={user.id} className="flex items-center justify-between">
                      <span className="text-sm text-amber-600 truncate">{user.email}</span>
                      <span className="text-xs text-slate-500 font-medium ml-2">
                        {user.videosLimit - user.videosRemaining} used / {user.videosRemaining} left
                      </span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-slate-400 text-sm">No users near limit</p>;
            })()}
          </div>

          {/* Newest Users */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <p className="text-sm text-slate-600 font-medium mb-3">Newest Users</p>
            {(() => {
              const newest = [...stats.users].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5);
              return newest.length > 0 ? (
                <div className="space-y-2">
                  {newest.map((user) => (
                    <div key={user.id} className="flex items-center justify-between">
                      <span className="text-sm text-slate-900 truncate">{user.email}</span>
                      <span className="text-xs text-slate-400 ml-2">{timeAgo(user.createdAt)}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-slate-400 text-sm">No users</p>;
            })()}
          </div>

          {/* Conversion Funnel Card */}
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-sm font-medium text-slate-500 mb-4">Conversion Funnel</h3>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Upgrade Clicks</span>
                <span className="text-lg font-semibold text-slate-900">
                  {stats?.eventFunnel?.upgradeClicks || 0}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Checkout Started</span>
                <span className="text-lg font-semibold text-slate-900">
                  {stats?.eventFunnel?.checkoutStarted || 0}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Purchases</span>
                <span className="text-lg font-semibold text-green-600">
                  {stats?.eventFunnel?.checkoutCompleted || 0}
                </span>
              </div>

              <div className="border-t border-slate-100 pt-3 mt-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">Conversion Rate</span>
                  <span className="text-xl font-bold text-slate-900">
                    {stats?.eventFunnel?.conversionRate || '0%'}
                  </span>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-3 mt-3 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Manage Subs Clicks</span>
                  <span className="text-slate-700">{stats?.eventFunnel?.manageSubscriptionClicks || 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Cancel Initiated</span>
                  <span className="text-slate-700">{stats?.eventFunnel?.cancelInitiated || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Accounting / Financial Overview */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Financial Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {(() => {
              const costs = stats.pricing?.costs ?? { VIDEO_GENERATION: 0.05, IMAGE_ANALYSIS: 0.22 };
              const proPrice = (stats.pricing?.planLimits?.pro?.price ?? 4900) / 100;
              const videoCost = stats.summary.totalGenerations * costs.VIDEO_GENERATION;
              const imageCost = stats.summary.totalImageAnalyses * costs.IMAGE_ANALYSIS;
              const totalCost = videoCost + imageCost;
              const revenue = stats.summary.activeProSubscriptions * proPrice;

              return (
                <>
                  <div>
                    <p className="text-sm text-slate-500">Video Cost</p>
                    <p className="text-xl font-semibold text-slate-900">${videoCost.toFixed(2)}</p>
                    <p className="text-xs text-slate-400">{stats.summary.totalGenerations} x ${costs.VIDEO_GENERATION}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Image Cost</p>
                    <p className="text-xl font-semibold text-slate-900">${imageCost.toFixed(2)}</p>
                    <p className="text-xs text-slate-400">{stats.summary.totalImageAnalyses} x ${costs.IMAGE_ANALYSIS}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Cost</p>
                    <p className="text-xl font-semibold text-red-600">${totalCost.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Revenue (Pro x ${proPrice})</p>
                    <p className="text-xl font-semibold text-emerald-600">${revenue.toFixed(2)}</p>
                    <p className="text-xs text-slate-400">{stats.summary.activeProSubscriptions} Pro users</p>
                  </div>
                </>
              );
            })()}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
            {(() => {
              const costs = stats.pricing?.costs ?? { VIDEO_GENERATION: 0.05, IMAGE_ANALYSIS: 0.22 };
              const proPrice = (stats.pricing?.planLimits?.pro?.price ?? 4900) / 100;
              const videoCost = stats.summary.totalGenerations * costs.VIDEO_GENERATION;
              const imageCost = stats.summary.totalImageAnalyses * costs.IMAGE_ANALYSIS;
              const totalCost = videoCost + imageCost;
              const revenue = stats.summary.activeProSubscriptions * proPrice;
              const profit = revenue - totalCost;
              const isProfit = profit >= 0;

              return (
                <>
                  <div>
                    <p className="text-sm text-slate-500">Net {isProfit ? 'Profit' : 'Loss'}</p>
                    <p className={`text-2xl font-bold ${isProfit ? 'text-emerald-600' : 'text-red-600'}`}>
                      {isProfit ? '+' : ''}{profit.toFixed(2)} USD
                    </p>
                  </div>
                  <div className={`px-4 py-2 rounded-lg ${isProfit ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                    <p className="text-sm font-medium">{isProfit ? 'Profitable' : 'Operating at Loss'}</p>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        {/* Conversion Metrics */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Conversion Metrics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {(() => {
              const freeUsers = stats.users.filter(u => u.subscriptionPlan === 'free').length;
              const proUsers = stats.users.filter(u => u.subscriptionPlan === 'pro' && !TEST_ACCOUNTS.includes(u.email)).length;
              const conversionRate = stats.summary.totalUsers > 0 ? ((proUsers / stats.summary.totalUsers) * 100).toFixed(1) : '0';
              const bannedUsers = stats.users.filter(u => u.isBanned).length;

              return (
                <>
                  <div>
                    <p className="text-sm text-slate-500">Free Users</p>
                    <p className="text-xl font-semibold text-slate-900">{freeUsers}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Pro Users</p>
                    <p className="text-xl font-semibold text-violet-600">{proUsers}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Conversion Rate</p>
                    <p className="text-xl font-semibold text-emerald-600">{conversionRate}%</p>
                    <p className="text-xs text-slate-400">Free to Pro</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Banned Users</p>
                    <p className="text-xl font-semibold text-red-600">{bannedUsers}</p>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        {/* Users Table */}
        <div className="mb-8">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Users</h2>
            <p className="text-sm text-slate-400">Click column headers to sort</p>
          </div>
          <UsersTable users={stats.users} dbEnv={dbEnvironment} />
        </div>

        {/* Recent Prompts */}
        <RecentPrompts
          prompts={stats.recentPrompts}
          pagination={stats.pagination ? {
            page: stats.pagination.page,
            totalPages: stats.pagination.totalPages,
          } : undefined}
          onPageChange={handlePromptsPageChange}
          users={stats.users}
          selectedUserId={promptsUserId}
          onUserFilterChange={handlePromptsUserFilterChange}
        />
      </div>
    </div>
  );
}
